import React, { useEffect, useState } from 'react'
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Badge,
} from '@mui/material'
import {
  Search,
  SupportAgent,
  ChatBubbleOutline,
  DoneAll,
  Done,
  AccessTime,
  SentimentVerySatisfied,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
  SentimentVeryDissatisfied,
  Dashboard,
  Psychology,
  Flag,
  Email,
  PushPin,
  Computer,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../../services/Main/Api'
import moment from 'moment'

const STATUS_COLORS = {
  OPEN: { bg: '#DBEAFE', color: '#1E40AF', label: 'Aberto' },
  IN_PROGRESS: { bg: '#FEF3C7', color: '#92400E', label: 'Em Andamento' },
  WAITING_CLIENT: { bg: '#FEE2E2', color: '#991B1B', label: 'Aguardando Cliente' },
  RESOLVED: { bg: '#D1FAE5', color: '#065F46', label: 'Finalizado' },
}

const PRIORITY_FLAGS = {
  LOW: { color: '#94A3B8', label: 'Baixa' },
  MEDIUM: { color: '#3B82F6', label: 'Média' },
  HIGH: { color: '#F59E0B', label: 'Alta' },
  URGENT: { color: '#EF4444', label: 'Urgente' },
}

const RATING_CONFIG = {
  5: { icon: SentimentVerySatisfied, label: 'Ótimo', color: '#16A34A' },
  4: { icon: SentimentSatisfied, label: 'Bom', color: '#65A30D' },
  3: { icon: SentimentNeutral, label: 'Regular', color: '#D97706' },
  2: { icon: SentimentDissatisfied, label: 'Ruim', color: '#EA580C' },
  1: { icon: SentimentVeryDissatisfied, label: 'Péssimo', color: '#DC2626' },
}

const AdminTicketList = () => {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')

  const fetchTickets = () => {
    setLoading(true)
    const params = {}
    if (search) params.search = search
    if (statusFilter) params.status = statusFilter
    if (priorityFilter) params.priority = priorityFilter

    api.get('tickets/', { params })
      .then(res => setTickets(res.data.results || res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchTickets()
  }, [statusFilter, priorityFilter])

  const handleSearch = (e) => {
    e.preventDefault()
    fetchTickets()
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <SupportAgent sx={{ fontSize: 28, color: '#1E3040' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Tickets de Suporte
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie todos os chamados dos clientes
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Psychology />}
            onClick={() => navigate('/admin/tickets/knowledge')}
            sx={{ color: '#7C3AED', borderColor: '#DDD6FE', '&:hover': { borderColor: '#7C3AED', bgcolor: '#F5F3FF' }, textTransform: 'none' }}
          >
            Base IA
          </Button>
          <Button
            variant="outlined"
            startIcon={<Dashboard />}
            onClick={() => navigate('/admin/tickets/dashboard')}
            sx={{ color: '#475569', borderColor: '#CBD5E1', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' }, textTransform: 'none' }}
          >
            Dashboard
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', py: '12px !important' }}>
          <Box component="form" onSubmit={handleSearch} sx={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por protocolo, título, empresa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#94A3B8' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="OPEN">Aberto</MenuItem>
              <MenuItem value="IN_PROGRESS">Em Andamento</MenuItem>
              <MenuItem value="WAITING_CLIENT">Aguardando Cliente</MenuItem>
              <MenuItem value="RESOLVED">Finalizado</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Prioridade</InputLabel>
            <Select value={priorityFilter} label="Prioridade" onChange={(e) => setPriorityFilter(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="LOW">Baixa</MenuItem>
              <MenuItem value="MEDIUM">Média</MenuItem>
              <MenuItem value="HIGH">Alta</MenuItem>
              <MenuItem value="URGENT">Urgente</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nº do Ticket</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tempo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Título</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Categoria</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Prioridade</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pessoas</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Conversas</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Nota</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Atualização</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(10)].map((_, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <SupportAgent sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      Nenhum ticket encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map(ticket => {
                  const statusStyle = STATUS_COLORS[ticket.status] || STATUS_COLORS.OPEN
                  const priorityFlag = PRIORITY_FLAGS[ticket.priority] || PRIORITY_FLAGS.MEDIUM
                  const highlighted = ticket.needs_attention || ticket.is_pinned
                  return (
                    <TableRow
                      key={ticket.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        ...(ticket.needs_attention && {
                          bgcolor: '#F0F7FF',
                          borderLeft: '3px solid #0073ea',
                        }),
                        ...(!ticket.needs_attention && ticket.is_pinned && {
                          bgcolor: '#FFFBEB',
                          borderLeft: '3px solid #F59E0B',
                        }),
                      }}
                      onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {ticket.needs_attention && (
                            <Box sx={{
                              width: 8, height: 8, borderRadius: '50%', bgcolor: '#0073ea', flexShrink: 0,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { boxShadow: '0 0 0 0 rgba(0,115,234,0.4)' },
                                '70%': { boxShadow: '0 0 0 6px rgba(0,115,234,0)' },
                                '100%': { boxShadow: '0 0 0 0 rgba(0,115,234,0)' },
                              },
                            }} />
                          )}
                          {!ticket.needs_attention && ticket.is_pinned && (
                            <Tooltip title="Em acompanhamento" arrow>
                              <PushPin sx={{ fontSize: 14, color: '#F59E0B', flexShrink: 0 }} />
                            </Tooltip>
                          )}
                          <Typography variant="body2" sx={{ fontWeight: highlighted ? 700 : 600, fontFamily: 'monospace', color: ticket.needs_attention ? '#0073ea' : ticket.is_pinned ? '#92400E' : 'inherit' }}>
                            {ticket.protocol}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={ticket.support_time ? `Tempo do suporte: ${ticket.support_time}` : 'Sem interações'}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTime sx={{ fontSize: 14, color: ticket.sla_status === 'overdue' ? '#DC2626' : ticket.sla_status === 'warning' ? '#D97706' : '#94A3B8' }} />
                            <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: ticket.sla_status === 'overdue' ? '#DC2626' : ticket.sla_status === 'warning' ? '#D97706' : 'text.secondary' }}>
                              {ticket.support_time || ticket.elapsed_time}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: highlighted ? 700 : 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.condo?.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          {ticket.category?.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={priorityFlag.label} arrow>
                          <Flag sx={{ fontSize: 20, color: priorityFlag.color }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={statusStyle.label} size="small" sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600, fontSize: 12 }} />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const people = []
                          if (ticket.created_by) people.push(ticket.created_by)
                          if (ticket.assigned_to && ticket.assigned_to.id !== ticket.created_by?.id) people.push(ticket.assigned_to)
                          return (
                            <AvatarGroup max={3} sx={{ justifyContent: 'flex-end', '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12, border: '2px solid #fff' } }}>
                              {people.map(p => (
                                <Tooltip key={p.id} title={`${p.first_name} ${p.last_name}${p.is_superuser ? ' (Equipe B3)' : ''}`} arrow>
                                  <Avatar
                                    src={p.photo}
                                    sx={{
                                      bgcolor: p.is_superuser ? '#0073ea' : '#94A3B8',
                                      ...(p.is_superuser && { boxShadow: '0 0 0 2px #0073ea' }),
                                    }}
                                  >
                                    {p.first_name?.[0]}{p.last_name?.[0]}
                                  </Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                          )
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const lastStaff = ticket.last_staff_comment_at ? moment(ticket.last_staff_comment_at) : null
                          const seenWeb = ticket.client_last_read_at && lastStaff && moment(ticket.client_last_read_at).isAfter(lastStaff)
                          const seenEmail = ticket.client_email_read_at && lastStaff && moment(ticket.client_email_read_at).isAfter(lastStaff)
                          const hasStaffReply = !!lastStaff
                          return (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <ChatBubbleOutline sx={{ fontSize: 16, color: ticket.unread_count ? '#0073ea' : '#94A3B8' }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: ticket.unread_count ? '#0073ea' : '#94A3B8', fontSize: 13 }}>
                                {ticket.comment_count || 0}
                              </Typography>
                              {seenWeb && (
                                <Tooltip title={`Visto na plataforma ${moment(ticket.client_last_read_at).fromNow()}`} arrow>
                                  <Computer sx={{ fontSize: 14, color: '#0073ea' }} />
                                </Tooltip>
                              )}
                              {seenEmail && (
                                <Tooltip title={`Visto no email ${moment(ticket.client_email_read_at).fromNow()}`} arrow>
                                  <Email sx={{ fontSize: 14, color: '#22C55E' }} />
                                </Tooltip>
                              )}
                              {hasStaffReply && !seenWeb && !seenEmail && (
                                <Tooltip title="Cliente não visualizou a última resposta" arrow>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#CBD5E1', ml: 0.3 }} />
                                </Tooltip>
                              )}
                            </Box>
                          )
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {ticket.feedback_rating ? (
                          (() => {
                            const cfg = RATING_CONFIG[ticket.feedback_rating] || RATING_CONFIG[3]
                            const Icon = cfg.icon
                            return (
                              <Tooltip title={`${cfg.label} (${ticket.feedback_rating}/5)`} arrow>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                  <Icon sx={{ fontSize: 22, color: cfg.color }} />
                                </Box>
                              </Tooltip>
                            )
                          })()
                        ) : (
                          <Typography variant="caption" sx={{ color: '#CBD5E1' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={moment(ticket.last_activity_at || ticket.created_at).format('DD/MM/YYYY [às] HH:mm')} arrow>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            {moment(ticket.last_activity_at || ticket.created_at).fromNow()}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  )
}

export default AdminTicketList
